import React, { useRef, useState, useEffect, useCallback } from "react";
import WaveSurfer from "wavesurfer.js";
import "../scss/audioPlayer.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faClock } from "@fortawesome/free-solid-svg-icons";

const AudioPlayer = ({ audioUrl, isPlaying, setIsPlaying }) => {
  const waveSurferRef = useRef(null);
  const waveformContainerRef = useRef(null);
  const [audioDuration, setAudioDuration] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (waveformContainerRef.current && !waveSurferRef.current) {
      waveSurferRef.current = WaveSurfer.create({
        container: waveformContainerRef.current,
        waveColor: "#d9dcf1",
        progressColor: "rgba(130, 158, 209, 1)",
        backend: "WebAudio",
        height: 60,
        barWidth: 1,
        barGap: 1,
        barRadius: 0,
        responsive: true,
        normalize: true,
        interact: true,
      });

      waveSurferRef.current.load(audioUrl);

      waveSurferRef.current.on("ready", () => {
        const duration = waveSurferRef.current.getDuration();
        if (duration && duration > 0) {
          setAudioDuration(duration);
        }
      });

      waveSurferRef.current.on("audioprocess", () => {
        setCurrentTime(waveSurferRef.current.getCurrentTime());
      });

      waveSurferRef.current.on("finish", () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });

      waveSurferRef.current.on("error", (e) => {
        console.error("WaveSurfer error:", e);
      });
    }

    return () => {
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy();
        waveSurferRef.current = null;
      }
    };
  }, [audioUrl]);

  const handleSeek = useCallback((position) => {
    if (waveSurferRef.current) {
      const normalizedPosition = Math.max(0, Math.min(1, position));
      waveSurferRef.current.seekTo(normalizedPosition);
      waveSurferRef.current.play();
      setIsPlaying(true);
    }
  }, [setIsPlaying]);

  useEffect(() => {
    const container = waveformContainerRef.current;
    if (!container) return;

    const handleInteraction = (event) => {
      if (!waveSurferRef.current) return;

      const rect = container.getBoundingClientRect();
      const position = (event.clientX - rect.left) / rect.width;
      handleSeek(position);
    };

    container.addEventListener("click", handleInteraction);

    return () => {
      try {
        container.removeEventListener("click", handleInteraction);
      } catch (err) {
        console.warn("Failed to remove click listener:", err);
      }
    };
  }, [handleSeek]);

  const togglePlayPause = () => {
    if (waveSurferRef.current) {
      if (isPlaying) {
        waveSurferRef.current.pause();
      } else {
        waveSurferRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    if (!isPlaying && waveSurferRef.current) {
      waveSurferRef.current.pause();
    }
  }, [isPlaying]);

  const formatDuration = (duration) => {
    if (!duration || isNaN(duration)) return "0:00";
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="audio-player">
      <button className="play-pause-button" onClick={togglePlayPause} title={isPlaying ? "Pause" : "Play"}>
        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
      </button>
      <div className="waveform-container" ref={waveformContainerRef}></div>
      <div className="duration-display">
        <FontAwesomeIcon icon={faClock} title="Duration" />
        <span className="duration-text">
          {audioDuration ? `${formatDuration(currentTime)} / ${formatDuration(audioDuration)}` : "Loading..."}
        </span>
      </div>
    </div>
  );
};

export default AudioPlayer;
